// src/components/titlebar/SigmaMainTitleBar.tsx

import React from "react"
import { Home as HomeIcon, Refresh, OpenInNew as OpenInNewIcon, Help as HelpIcon } from "@mui/icons-material"
import { useSelector } from "react-redux"
import { getPlatformBridge } from "@core/platform"
import { GlobalSnackbarProvider } from "@core/contexts/GlobalSnackbarProvider"
import { hospitalInfoSelector } from "@/features/hospital/hospitalSelectors"
import { memberSelector } from "@/features/member/memberSelectors"
import { selectedPatientSelector, selectedInsuranceSelector } from "@/features/patient/patientSelectors"
import { useServerUpdateInProgress } from "@core/hooks/useServerUpdateInProgress"
import { clearServerUpdateInProgress, markServerUpdateInProgress } from "@core/utils/serverConnectionBootstrap"
import InquiryLayer from "@shared/components/modals/inquiry/InquiryLayer"
import { isWebPlatform } from "@/core/platform"
import { openReservedInquiryDetachedPopup } from "@shared/components/modals/inquiry/InquiryDetachedPortal"
import { useTitleBarMenu } from "./hooks/useTitleBarMenu"
import { useTitleBarActions } from "./hooks/useTitleBarActions"
import { TitleBarContainer, LeftSection, CenterSection, RightSection, TitleText, HomeButton, InquiryButton } from "./styles/titleBarStyles"
import ServerMenu from "./components/ServerMenu"
import ViewMenu from "./components/ViewMenu"
import HelpMenu from "./components/HelpMenu"
import ServerUpdateDialog from "./components/ServerUpdateDialog"
import WindowControls from "./components/WindowControls"
import ZoomControls from "./components/ZoomControls"
import ServerSearchDialog from "./components/ServerSearchDialog"
import type { ServerUpdateInfo } from "./types/titleBarTypes"

const SIGMA_AGENT_PATH = "C:\\Ninetynine\\agent\\SigmaAgent.exe"
const SIGMA_AGENT_WORKING_DIRECTORY = "C:\\Ninetynine\\agent"

const SigmaMainTitleBar: React.FC = () => {
  const { state, setState } = useTitleBarMenu()
  const actions = useTitleBarActions()
  const [serverUpdateDialogOpen, setServerUpdateDialogOpen] = React.useState(false)
  const [serverUpdateLoading, setServerUpdateLoading] = React.useState(false)
  const [serverUpdateInfo, setServerUpdateInfo] = React.useState<ServerUpdateInfo | null>(null)
  const [serverUpdateStarting, setServerUpdateStarting] = React.useState(false)
  const [isWebChartAvailable, setIsWebChartAvailable] = React.useState(false)
  const [inquiryLayerOpen, setInquiryLayerOpen] = React.useState(false)
  const [inquiryLayerResetTrigger, setInquiryLayerResetTrigger] = React.useState(0)
  const [inquiryLayerActivationKey, setInquiryLayerActivationKey] = React.useState(0)
  const [inquiryReservedPopupWindow, setInquiryReservedPopupWindow] = React.useState<Window | null>(null)
  const autoCheckedServerUpdateBusinessNoRef = React.useRef<string | null>(null)
  const serverUpdateExecutionLockRef = React.useRef(false)
  const { inProgress: isServerUpdateInProgress } = useServerUpdateInProgress()

  // Redux에서 병원 정보 가져오기
  const hospitalInfo = useSelector(hospitalInfoSelector)
  const member = useSelector(memberSelector)
  const selectedPatient = useSelector(selectedPatientSelector)
  const selectedInsurance = useSelector(selectedInsuranceSelector)
  const isSuperAdmin = Boolean(member?.isSuperuser || member?.memberGrant === "최고관리자")

  React.useEffect(() => {
    const businessNo = hospitalInfo?.hospitalBusinessNo || null

    if (!isSuperAdmin || !businessNo || !window.electronAPI?.getServerUpdateInfo || isServerUpdateInProgress) {
      if (!isSuperAdmin) {
        autoCheckedServerUpdateBusinessNoRef.current = null
      }
      return
    }

    if (autoCheckedServerUpdateBusinessNoRef.current === businessNo) {
      return
    }

    autoCheckedServerUpdateBusinessNoRef.current = businessNo

    let isMounted = true

    const checkServerUpdateOnLogin = async () => {
      try {
        const result = await window.electronAPI.getServerUpdateInfo({
          businessNo,
        })

        if (!isMounted) {
          return
        }

        if (result?.updateAvailable && !result?.error) {
          setServerUpdateInfo(result)
          setServerUpdateDialogOpen(true)
        }
      } catch (error) {
        console.error("로그인 직후 서버 업데이트 정보 확인 오류:", error)
      }
    }

    void checkServerUpdateOnLogin()

    return () => {
      isMounted = false
    }
  }, [hospitalInfo?.hospitalBusinessNo, isServerUpdateInProgress, isSuperAdmin])

  React.useEffect(() => {
    const savedServerIp = localStorage.getItem("SigmaServerIp")
    const currentServer = savedServerIp || state.currentServer || ""
    const normalizedServer = currentServer
      .trim()
      .replace(/^https?:\/\//i, "")
      .replace(/\/$/, "")
      .replace(/:\d+$/, "")

    if (!normalizedServer || normalizedServer === "서버 미연결") {
      setIsWebChartAvailable(false)
      return
    }

    let isMounted = true

    const checkWebChartAvailability = async () => {
      try {
        const bridge = getPlatformBridge()
        let available = false

        if (bridge.platform === "electron") {
          available = await bridge.checkServer(normalizedServer, 443)
        } else {
          await fetch(`https://${normalizedServer}`, {
            method: "GET",
            mode: "no-cors",
            signal: AbortSignal.timeout(3000),
          })
          available = true
        }

        if (isMounted) {
          setIsWebChartAvailable(available)
        }
      } catch {
        if (isMounted) {
          setIsWebChartAvailable(false)
        }
      }
    }

    void checkWebChartAvailability()

    return () => {
      isMounted = false
    }
  }, [state.currentServer])

  // 서버 메뉴 핸들러
  const handleServerMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (state.serverMenuAnchor) {
      setState(prev => ({ ...prev, serverMenuAnchor: null }))
    } else {
      setState(prev => ({
        ...prev,
        serverMenuAnchor: event.currentTarget,
        serverConnectMenuAnchor: null,
        viewMenuAnchor: null,
        helpMenuAnchor: null,
      }))
    }
  }

  const handleServerMenuClose = () => {
    setState(prev => ({ ...prev, serverMenuAnchor: null }))
  }

  const handleServerConnectMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setState(prev => ({
      ...prev,
      viewMenuAnchor: null,
      helpMenuAnchor: null,
      serverConnectMenuAnchor: event.currentTarget,
    }))
  }

  const handleServerConnectMenuClose = () => {
    setState(prev => ({ ...prev, serverConnectMenuAnchor: null }))
  }

  const handleServerConnect = async () => {
    try {
      const currentIp = localStorage.getItem("SigmaServerIp")
      if (currentIp) {
        console.log(`✅ 기본서버 연결: ${currentIp}`)
        handleServerConnectMenuClose()
        handleServerMenuClose()
        return
      }

      const result = await actions.executeMenuAction("server-connect")
      if (result?.success) {
        const savedServer = localStorage.getItem("SigmaServerIp")
        setState(prev => ({
          ...prev,
          currentServer: savedServer || result.serverIp || "",
        }))
      }
    } catch (error) {
      console.error("서버 연결 오류:", error)
    }

    handleServerConnectMenuClose()
    handleServerMenuClose()
  }

  const handleServerSearch = async () => {
    setState(prev => ({
      ...prev,
      isSearching: true,
      searchResultDialogOpen: true,
      searchResults: [],
    }))

    try {
      const result = await actions.executeMenuAction("server-search")
      if (result?.success && result.servers) {
        setState(prev => ({
          ...prev,
          discoveredServers: result.servers,
          searchResults: result.servers,
        }))
      } else {
        setState(prev => ({ ...prev, searchResults: [] }))
      }
    } catch (error) {
      console.error("서버 검색 오류:", error)
      setState(prev => ({ ...prev, searchResults: [] }))
    } finally {
      setState(prev => ({ ...prev, isSearching: false }))
    }

    handleServerMenuClose()
  }

  const handleConnectToServer = async (serverIp: string) => {
    const updateSelectionLabel = () => {
      setState(prev => ({
        ...prev,
        currentServer: serverIp,
        currentServerPcName: null,
      }))

      try {
        localStorage.setItem("SigmaServerIp", serverIp)
        localStorage.removeItem("SigmaServerPcName")
      } catch (storageError) {
        console.error("서버 선택 상태 저장 실패:", storageError)
      } finally {
        window.dispatchEvent(new Event("storage"))
      }
    }

    updateSelectionLabel()

    const result = await actions.executeMenuAction("server-connect", { serverIp })
    if (result?.success) {
      const savedServer = localStorage.getItem("SigmaServerIp")
      setState(prev => ({
        ...prev,
        currentServer: savedServer || serverIp,
      }))
    }
    setState(prev => ({ ...prev, searchResultDialogOpen: false }))
    handleServerMenuClose()
  }

  // View 메뉴 핸들러
  const handleViewMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (state.viewMenuAnchor) {
      setState(prev => ({ ...prev, viewMenuAnchor: null }))
    } else {
      setState(prev => ({
        ...prev,
        viewMenuAnchor: event.currentTarget,
        serverMenuAnchor: null,
        serverConnectMenuAnchor: null,
        helpMenuAnchor: null,
      }))
    }
  }

  const handleViewMenuClose = () => {
    setState(prev => ({ ...prev, viewMenuAnchor: null }))
  }

  const handleReload = async () => {
    try {
      await window.electronAPI?.executeMenuAction?.("reload")
    } catch (error) {
      console.error("새로고침 오류:", error)
    }
    handleViewMenuClose()
  }

  const handleForceReload = async () => {
    try {
      await window.electronAPI?.executeMenuAction?.("force-reload")
    } catch (error) {
      console.error("강제 새로고침 오류:", error)
    }
    handleViewMenuClose()
  }

  const handleToggleDevTools = async () => {
    try {
      await window.electronAPI?.executeMenuAction?.("toggle-devtools")
    } catch (error) {
      console.error("개발자 도구 토글 오류:", error)
    }
    handleViewMenuClose()
  }

  const handleToggleFullscreen = async () => {
    try {
      await window.electronAPI?.executeMenuAction?.("toggle-fullscreen")
    } catch (error) {
      console.error("전체화면 토글 오류:", error)
    }
    handleViewMenuClose()
  }

  // Help 메뉴 핸들러
  const handleHelpMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (state.helpMenuAnchor) {
      setState(prev => ({ ...prev, helpMenuAnchor: null }))
    } else {
      setState(prev => ({
        ...prev,
        helpMenuAnchor: event.currentTarget,
        serverMenuAnchor: null,
        serverConnectMenuAnchor: null,
        viewMenuAnchor: null,
      }))
    }
  }

  const handleHelpMenuClose = () => {
    setState(prev => ({ ...prev, helpMenuAnchor: null }))
  }

  const handleShowVersion = async () => {
    try {
      await window.electronAPI?.executeMenuAction?.("show-version")
    } catch (error) {
      console.error("버전 정보 표시 오류:", error)
    }
    handleHelpMenuClose()
  }

  const handleShowVersionFeatures = () => {
    window.dispatchEvent(
      new CustomEvent("open-nnc-tips", {
        detail: {
          noticeId: "76c35b7d-f9d1-4413-a631-a4121c6c0c92",
        },
      })
    )
    handleHelpMenuClose()
  }

  const handleShowServerUpdate = async () => {
    handleHelpMenuClose()

    if (!window.electronAPI?.getServerUpdateInfo) {
      return
    }

    setServerUpdateDialogOpen(true)

    if (isServerUpdateInProgress) {
      return
    }

    setServerUpdateLoading(true)
    setServerUpdateInfo(null)

    try {
      const result = await window.electronAPI.getServerUpdateInfo({
        businessNo: hospitalInfo?.hospitalBusinessNo || null,
      })
      setServerUpdateInfo(result)
    } catch (error) {
      console.error("서버 업데이트 정보 확인 오류:", error)
      setServerUpdateInfo({
        success: false,
        isInstalled: true,
        currentVersion: null,
        latestVersion: null,
        updateAvailable: false,
        error: "서버 버전 정보를 확인하지 못했습니다.",
        errorContext: "version_check",
      })
    } finally {
      setServerUpdateLoading(false)
    }
  }

  const handleServerUpdate = async () => {
    if (serverUpdateExecutionLockRef.current || isServerUpdateInProgress) {
      return
    }

    const updateBusinessNo = hospitalInfo?.hospitalBusinessNo || null

    serverUpdateExecutionLockRef.current = true
    setServerUpdateStarting(true)

    markServerUpdateInProgress(undefined, {
      businessNo: updateBusinessNo,
      phase: "before_execute_external_program",
    })
    setServerUpdateDialogOpen(false)

    try {
      const result = await actions.executeMenuAction("execute-external-program", {
        programPath: SIGMA_AGENT_PATH,
        args: ["--server_update"],
        cwd: SIGMA_AGENT_WORKING_DIRECTORY,
        runAsAdmin: true,
      })

      if (result?.success) {
        return
      }

      setServerUpdateInfo(previousInfo => ({
        success: false,
        isInstalled: previousInfo?.isInstalled ?? true,
        currentVersion: previousInfo?.currentVersion ?? null,
        currentVersionSource: previousInfo?.currentVersionSource,
        latestVersion: previousInfo?.latestVersion ?? null,
        updateAvailable: previousInfo?.updateAvailable ?? false,
        error: result?.error || "서버 업데이트 실행에 실패했습니다.",
        errorContext: "update_execute",
      }))
      clearServerUpdateInProgress({
        businessNo: updateBusinessNo,
        error: result?.error || null,
        reason: "execute_external_program_failed",
      })
      setServerUpdateDialogOpen(true)
      console.error("서버 업데이트 실행 실패:", result?.error)
    } catch (error) {
      setServerUpdateInfo(previousInfo => ({
        success: false,
        isInstalled: previousInfo?.isInstalled ?? true,
        currentVersion: previousInfo?.currentVersion ?? null,
        currentVersionSource: previousInfo?.currentVersionSource,
        latestVersion: previousInfo?.latestVersion ?? null,
        updateAvailable: previousInfo?.updateAvailable ?? false,
        error: error instanceof Error ? error.message : String(error),
        errorContext: "update_execute",
      }))
      clearServerUpdateInProgress({
        businessNo: updateBusinessNo,
        error: error instanceof Error ? error.message : String(error),
        reason: "execute_external_program_error",
      })
      setServerUpdateDialogOpen(true)
      console.error("서버 업데이트 실행 오류:", error)
    } finally {
      serverUpdateExecutionLockRef.current = false
      setServerUpdateStarting(false)
    }
  }

  const handleBrowserReload = () => {
    // encounter 라우트에서 리로드 시 선택 환자 복원을 위해 sessionStorage에 저장
    if (selectedPatient?.patientUuid) {
      sessionStorage.setItem("reloadPatientUuid", selectedPatient.patientUuid)
      if (selectedInsurance?.insuranceUuid) {
        sessionStorage.setItem("reloadInsuranceUuid", selectedInsurance.insuranceUuid)
      }
    }
    window.location.reload()
  }

  const getWebChartUrl = () => {
    const savedServerIp = localStorage.getItem("SigmaServerIp")
    const currentServer = savedServerIp || state.currentServer || ""
    const normalizedServer = currentServer
      .trim()
      .replace(/^https?:\/\//i, "")
      .replace(/\/$/, "")
      .replace(/:\d+$/, "")

    if (!normalizedServer || normalizedServer === "서버 미연결") {
      return null
    }

    return `https://${normalizedServer}`
  }

  const getMobileWebChartUrl = () => {
    if (typeof window === "undefined") {
      return null
    }

    const currentUrl = new URL(window.location.href)
    if (currentUrl.hostname === "127.0.0.1") {
      currentUrl.hostname = "localhost"
    }

    const savedServerIp = localStorage.getItem("SigmaServerIp")?.trim() || ""
    const savedServerPcName = localStorage.getItem("SigmaServerPcName")?.trim() || ""

    currentUrl.searchParams.set("bootstrap", "1")

    if (savedServerIp) {
      currentUrl.searchParams.set("bootstrapServerIp", savedServerIp)
    } else {
      currentUrl.searchParams.delete("bootstrapServerIp")
    }

    if (savedServerPcName) {
      currentUrl.searchParams.set("bootstrapServerPcName", savedServerPcName)
    } else {
      currentUrl.searchParams.delete("bootstrapServerPcName")
    }

    return `${currentUrl.toString().split("#")[0]}#/mobile`
  }

  const handleOpenWebChart = async () => {
    const webChartUrl = getWebChartUrl()

    if (!webChartUrl) {
      console.error("웹차트 열기 오류: 현재 연결된 서버 IP가 없습니다.")
      return
    }

    try {
      if (window.electronAPI?.openSystemBrowserUrl) {
        await window.electronAPI.openSystemBrowserUrl(webChartUrl)
        return
      }

      const openedWindow = window.open(webChartUrl, "_blank", "noopener,noreferrer")
      if (openedWindow) {
        openedWindow.focus()
      }
    } catch (error) {
      console.error("웹차트 열기 오류:", error)
    }
  }

  const handleOpenMobileWebChart = async () => {
    const mobileWebChartUrl = getMobileWebChartUrl()

    if (!mobileWebChartUrl) {
      console.error("모바일 웹차트 열기 오류: 현재 앱 경로를 확인할 수 없습니다.")
      return
    }

    try {
      if (window.electronAPI?.openSystemBrowserUrl && /^https?:/i.test(mobileWebChartUrl)) {
        await window.electronAPI.openSystemBrowserUrl(mobileWebChartUrl)
        return
      }

      const openedWindow = window.open(mobileWebChartUrl, "_blank", "noopener,noreferrer")
      if (openedWindow) {
        openedWindow.focus()
      }
    } catch (error) {
      console.error("모바일 웹차트 열기 오류:", error)
    }
  }

  const handleOpenInquiry = React.useCallback(() => {
    const isWeb = isWebPlatform()
    const hasActivePopupWindow = Boolean(inquiryReservedPopupWindow && !inquiryReservedPopupWindow.closed)
    const reservedPopupWindow = isWeb ? (hasActivePopupWindow ? inquiryReservedPopupWindow : openReservedInquiryDetachedPopup()) : null

    if (isWeb) {
      setInquiryReservedPopupWindow(reservedPopupWindow)

      if (!hasActivePopupWindow && reservedPopupWindow === null) {
        alert("브라우저 팝업 차단을 해제한 뒤 다시 시도해 주세요.")
        return
      }
    }

    setInquiryLayerActivationKey(prev => prev + 1)

    if (inquiryLayerOpen) {
      return
    }

    setInquiryLayerResetTrigger(prev => prev + 1)
    setInquiryLayerOpen(true)
  }, [inquiryLayerOpen, inquiryReservedPopupWindow])

  const handleCloseInquiry = React.useCallback(() => {
    setInquiryLayerOpen(false)
    setInquiryReservedPopupWindow(null)
  }, [])

  return (
    <TitleBarContainer data-capture="titlebar">
      <LeftSection>
        <ServerMenu
          anchorEl={state.serverMenuAnchor}
          currentServerPcName={state.currentServerPcName}
          currentServer={state.currentServer}
          discoveredServers={state.discoveredServers}
          hospitalName={hospitalInfo?.hospitalName}
          onOpen={handleServerMenuOpen}
          onClose={handleServerMenuClose}
          onServerConnectMenuOpen={handleServerConnectMenuOpen}
          onServerConnectMenuClose={handleServerConnectMenuClose}
          onServerConnect={handleServerConnect}
          onServerSearch={handleServerSearch}
          onConnectToServer={handleConnectToServer}
          serverConnectMenuAnchor={state.serverConnectMenuAnchor}
        />

        <ViewMenu
          anchorEl={state.viewMenuAnchor}
          isDevelopmentMode={state.isDevelopmentMode}
          onOpen={handleViewMenuOpen}
          onClose={handleViewMenuClose}
          onReload={handleReload}
          onForceReload={handleForceReload}
          onToggleDevTools={handleToggleDevTools}
          onResetZoom={actions.handleResetZoom}
          onZoomIn={actions.handleZoomIn}
          onZoomOut={actions.handleZoomOut}
          onToggleFullscreen={handleToggleFullscreen}
        />

        <HomeButton startIcon={<HomeIcon sx={{ fontSize: 14 }} />} onClick={actions.handleGoHome}>
          첫화면 가기
        </HomeButton>
        <InquiryButton startIcon={<HelpIcon sx={{ fontSize: 14 }} />} onClick={handleOpenInquiry}>
          문의하기
        </InquiryButton>

        <ZoomControls onZoomIn={actions.handleZoomIn} onZoomOut={actions.handleZoomOut} />
        <Refresh
          sx={{ color: "gray", cursor: "pointer", marginLeft: 1, WebkitAppRegion: "no-drag" }}
          fontSize="small"
          onClick={handleBrowserReload}
        />
      </LeftSection>

      <CenterSection>
        <TitleText>시그마차트 {hospitalInfo?.hospitalName ? `${hospitalInfo.hospitalName}` : "미연결"}</TitleText>
      </CenterSection>

      <RightSection>
        {state.isDevelopmentMode && (
          <HomeButton startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} onClick={() => void handleOpenMobileWebChart()}>
            모바일보기
          </HomeButton>
        )}

        {isWebChartAvailable && (
          <HomeButton startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} onClick={() => void handleOpenWebChart()}>
            새창열기(웹차트 열기)
          </HomeButton>
        )}

        <HelpMenu
          anchorEl={state.helpMenuAnchor}
          onOpen={handleHelpMenuOpen}
          onClose={handleHelpMenuClose}
          onShowVersion={handleShowVersion}
          onShowVersionFeatures={handleShowVersionFeatures}
          showServerUpdate={isSuperAdmin}
          onShowServerUpdate={() => {
            void handleShowServerUpdate()
          }}
        />

        <WindowControls onMinimize={actions.handleMinimize} onMaximize={actions.handleMaximize} onClose={actions.handleClose} />
      </RightSection>

      <ServerUpdateDialog
        open={serverUpdateDialogOpen}
        loading={serverUpdateLoading}
        info={serverUpdateInfo}
        onClose={() => setServerUpdateDialogOpen(false)}
        onUpdate={() => {
          void handleServerUpdate()
        }}
        updateActionEnabled={serverUpdateInfo?.currentVersionSource === "local_file"}
        updateInProgress={isServerUpdateInProgress || serverUpdateStarting}
      />

      <ServerSearchDialog
        open={state.searchResultDialogOpen}
        isSearching={state.isSearching}
        searchResults={state.searchResults}
        onClose={() => setState(prev => ({ ...prev, searchResultDialogOpen: false }))}
        onSearch={handleServerSearch}
        onConnectToServer={handleConnectToServer}
      />

      <GlobalSnackbarProvider>
        <InquiryLayer
          open={inquiryLayerOpen}
          onClose={handleCloseInquiry}
          resetTrigger={inquiryLayerResetTrigger}
          activationKey={inquiryLayerActivationKey}
          reservedPopupWindow={inquiryReservedPopupWindow}
          preferDetachedOnWeb={isWebPlatform()}
        />
      </GlobalSnackbarProvider>
    </TitleBarContainer>
  )
}

export default SigmaMainTitleBar

